import { PROJECT_PERMISSIONS } from '@inference.engine/permissions/web';
import { Button, Empty, EmptyContent, EmptyDescription, EmptyHeader } from '@monksflow/monks-ui';
import { PlusIcon } from 'lucide-react';
import { ScopedPermission } from '@/shared/ui';
import { ActionButton, EmptyStateTitle } from '@/shared/ui';
import { briefsIntroDescription } from '../../briefs.constants';
import { useTranslation } from 'react-i18next';

interface BriefsEmptyStateProps {
  /**
   * When `true`, the user has an active search term and/or status filter and
   * zero matches were returned — renders the "no matches" copy variant (no
   * create CTA). Defaults to `false` (initial empty list — "No briefs yet").
   */
  hasActiveFilters?: boolean;
  /**
   * Fires when the user clicks the "New brief" CTA. Only rendered in the
   * default (no-filters) variant — a "create" action is meaningless for a
   * zero-results filter. When omitted, no button is shown.
   */
  onNewBrief?: () => void;
  /**
   * Fires when the user clicks "Clear filters" in the no-results variant
   * (resets search + status). When omitted, no clear button is shown.
   */
  onClearFilters?: () => void;
}

/**
 * Empty state for the briefs list view. Iconless + vertically centered to
 * match the Projects list empty states (`flex-1` fills the table container so
 * `Empty`'s `justify-center` centers the content).
 *
 * Pure presentational. Rendered by `BriefsTable` when the query resolves with
 * `items: []`. Two copy variants:
 *   - `hasActiveFilters=false` (default) → replicates the page header ("Briefs"
 *     + the intro description) + a primary "New brief" CTA (when `onNewBrief`
 *     is provided), mirroring the Projects list empty state.
 *   - `hasActiveFilters=true` → "No matching briefs" + "Try a different search
 *     or filter." (no CTA — creating doesn't clear a filter).
 *
 * `role="status"` is provided so screen readers announce the change of state.
 */
export function BriefsEmptyState({
  hasActiveFilters = false,
  onNewBrief,
  onClearFilters,
}: BriefsEmptyStateProps = {}) {
  const { t } = useTranslation('projects');

  if (hasActiveFilters) {
    return (
      <Empty role="status" data-testid="briefs-empty-state" className="flex-1">
        <EmptyHeader>
          <EmptyStateTitle>{t('projectDetail.briefsEmptyState.noMatchingBriefs')}</EmptyStateTitle>
          <EmptyDescription>
            {t('projectDetail.briefsEmptyState.tryADifferentSearchOr')}
          </EmptyDescription>
        </EmptyHeader>
        {onClearFilters ? (
          <EmptyContent>
            <Button variant="outline" onClick={onClearFilters}>
              {t('projectDetail.briefsEmptyState.clearFilters')}
            </Button>
          </EmptyContent>
        ) : null}
      </Empty>
    );
  }

  return (
    <Empty role="status" data-testid="briefs-empty-state" className="flex-1">
      <EmptyHeader>
        <EmptyStateTitle>{t('projectDetail.briefsEmptyState.briefs')}</EmptyStateTitle>
        <EmptyDescription>{briefsIntroDescription()}</EmptyDescription>
      </EmptyHeader>
      {onNewBrief ? (
        <EmptyContent>
          <ScopedPermission permissions={[PROJECT_PERMISSIONS.ProjectEditDetails]}>
            <ActionButton onClick={onNewBrief}>
              <PlusIcon className="size-4" />
              {t('projectDetail.briefsEmptyState.newBrief')}
            </ActionButton>
          </ScopedPermission>
        </EmptyContent>
      ) : null}
    </Empty>
  );
}
